// src/pages/Setor4Page.jsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  Box, Paper, Typography, TextField, Button, Stack, Divider,
  FormControl, InputLabel, Select, MenuItem, Table, TableBody,
  TableCell, TableContainer, TableHead, TableRow, IconButton,
} from '@mui/material';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import { useThemeMode } from '../context/ThemeContext';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  Legend, LineChart, Line, CartesianGrid
} from 'recharts';
import { postRelatorio, getKpis, exportXlsx } from '../api/arrecadacao';
import { useNavigate } from 'react-router-dom';
import GraficosArrecad, { GraficosPassageiros } from './GraficosArrecad';



// ================= helpers =================
const toDdMmYyyy = (iso) => {
  if (!iso) return '';
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y}`;
};
const fmtBR = (n) => Number(n || 0).toLocaleString('pt-BR');
const fmtBRL = (n) => (n ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
const fmtPct = (n) => `${(n * 100).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}%`;
const saveBlob = (blob, name) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = name; a.click();
  URL.revokeObjectURL(url);
};

// Função de limpeza usando o dicionário
const cleanLinha = (linha) => {
  if (!linha) return '';
  const raw = String(linha).trim().replace(/^Resposta\s*/i, ''); // remove "Resposta"
  return LINHAS_MAP[raw] || raw; // se tiver no dicionário, retorna mapeado;
};

const PassengerTicker = ({ data, colors = {} }) => {
  const loop = [...data, ...data];
  return (
    <Box
      sx={{
        overflow: 'hidden',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
        px: 2,
        py: 1,
      }}
    >
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
          whiteSpace: 'nowrap',
          animation: 'scrollX 35s linear infinite',
          '&:hover': { animationPlayState: 'paused' },
          '@keyframes scrollX': {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(-50%)' },
          },
        }}
      >
        {loop.map((it, idx) => (
          <Box
            key={`${it.tipo}-${idx}`}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 1.5,
              fontSize: 16,
              fontWeight: 600,
              color: colors[it.key] || 'text.primary',
            }}
          >
            {/* bolinha colorida */}
            <Box
              sx={{
                width: 14,
                height: 14,
                borderRadius: '50%',
                bgcolor: colors[it.key] || 'grey.500',
                flexShrink: 0,
              }}
            />
            <span>{it.tipo}</span>
            <span style={{ fontWeight: 700 }}>{fmtBR(it.passageiros)}</span>
            <span style={{ fontSize: 14, opacity: 0.8 }}>
              ({(it.pct * 100).toFixed(2).replace('.', ',')}%)
            </span>
          </Box>
        ))}
      </Box>
    </Box>
  );
};

/**
 * Conversão numérica robusta:
 * - "1.234,56" -> 1234.56  (pt-BR)
 * - "1,234.56" -> 1234.56  (en-US)
 * - "22,5"     -> 22.5
 * - "22.5"     -> 22.5
 * - 22.5 (number) -> 22.5
 */
const toNum = (v) => {
  if (v == null) return 0;
  if (typeof v === 'number') return v;

  let s = String(v).trim();
  if (s.includes(',')) {
    // vírgula como decimal (pt-BR)
    s = s.replace(/\./g, '').replace(',', '.');
    // remove milhares e ajusta decimal
  } else {
    // ponto como decimal (en-US) — não remova o ponto decimal
    s = s.replace(/,/g, '');
    // remove vírgula de milhares
  }

  const n = Number(s);
  return Number.isFinite(n) ? n : 0;
};
const CHIP_COLORS = {
  abt: '#ec4899',
  comum: '#10b981',
  esco_grat: '#22c55e',
  escolar: '#8b5cf6',
  isentos: '#f59e0b',
  p_social: '#0ea5e9',
  pagante: '#ef4444',
  vt: '#6366f1',
};
const SIDEBAR_W = 300; // largura da lateral

// ===================== DICIONÁRIO DE LINHAS =====================
const LINHAS_MAP = {
  "005": "005", "011": "011", "012": "012", "014": "014", "029": "029",
  "031": "031", "034": "034", "035": "035", "051": "051", "054": "054",
  "055": "055", "202": "202", "207": "207", "208": "208", "209": "209",
  "301": "301", "304": "304", "307": "307", "308": "308", "315": "315",
  "323": "323", "340": "340", "342": "342", "343": "343", "344": "344",
  "350": "350", "355": "355", "356": "356", "402": "402", "403": "403",
  "422": "422", "427": "427", "443": "443", "446": "446", "455": "455",
  "457": "457", "458": "458", "500": "500",
  "550": "550", "626": "626",
  "640": "640",

  // com ponto
  "011.1": "011.1", "011.2": "011.2", "011.3": "011.3", "011.4": "011.4",
  "054.1": "054.1", "055.1": "055.1", "055.2": "055.2", "209.1": "209.1",
  "302.2": "302.2", "307.1": "307.1", "355.1": "355.1", "446.1": "446.1",
  "458.1": "458.1", "640.1": "640.1",

  // alfanuméricas
  "A025": "A025", "A030": "A030", "A033": "A033", "A059": "A059",
};
// ================= page ====================
export default function Setor4Page() {
  const navigate = useNavigate();
  // funções de apoio
  const fmtDate = (d) => d.toISOString().slice(0, 10);

  const today = new Date();
  const tomorrow = new Date(today);
  tomorrow.setDate(today.getDate() + 1);

  const handleVoltar = () => navigate('/menu');
  // filtros (inicializados conforme solicitado)
  const [dateIni, setDateIni] = useState(fmtDate(today));
  const [timeIni, setTimeIni] = useState('04:00');
  const [dateFim, setDateFim] = useState(fmtDate(tomorrow));
  const [timeFim, setTimeFim] = useState('03:59');
  const [linha, setLinha] = useState('');
  const [tipo, setTipo] = useState('');
  const [garage] = useState(3);
  const { mode, toggleTheme } = useThemeMode();


  // dados
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(false);

  const linhasOptions = useMemo(() => {
    const set = new Set(rows.map(r => cleanLinha(r.linha)).filter(Boolean));
    return [...set].sort((a, b) => {
      const na = Number(a), nb = Number(b);
      const an = Number.isFinite(na), bn = Number.isFinite(nb);
      if (an && bn) return na - nb;
      if (an) return -1;
      if (bn) return 1;
      return a.localeCompare(b);
    });
  }, [rows]);
  const params = useMemo(() => ({
    garage,
    dateIni: toDdMmYyyy(dateIni),
    dateFim: toDdMmYyyy(dateFim),
  }), [garage, dateIni, dateFim]);
  // filtro local por hora/linha/tipo
  const filtered = useMemo(() => {
    const start = new Date(`${dateIni}T${timeIni}:00`);
    const end = new Date(`${dateFim}T${timeFim}:59`);

    return rows.filter((r) => {
      // 1) Filtro por intervalo de datas/horas (apenas se conseguir parsear)
      const [d, m, yRest] = (r.dtInicio || '').split('/');
      const [y, hhmm] = (yRest || '').split(' ');
      const dt = new Date(`${y}-${m}-${d}T${(hhmm || '00:00')}:00`);

      // Se a data for válida,
      if (!Number.isNaN(dt.getTime())) {
        if (dt < start || dt > end) return false;
      }

      // 2) Filtro por linha (igualdade exata, já que vem do Select)
      if (linha && cleanLinha(r.linha) !== linha) return false;
      // 3) Filtro por tipo (ex.: comum, pagante, etc.)
      if (tipo && Number(r[`${tipo}_qtd`] || 0) <= 0) return false;
      return true;
    });
  }, [rows, dateIni, timeIni, dateFim, timeFim, linha, tipo]);
  // KPIs locais (respeitam o filtro aplicado)
  const localAgg = useMemo(() => {
    let passageiros = 0;
    const carros = new Set();

    for (const r of filtered) {
      passageiros +=
        (r.abt_qtd || 0) +
        (r.comum_qtd || 0) +
        (r.esco_grat_qtd || 0) +
        (r.escolar_qtd || 0) +
        (r.isentos_qtd || 0) +
        (r.p_social_qtd || 0) +
        (r.pagante_qtd || 0) +
        (r.vt_qtd || 0);

      if (r.carro != null && String(r.carro).trim() !== '') {
        carros.add(String(r.carro).trim());
      }
    }

    return {
      passageiros,
      viagens: filtered.length, // nº de linhas do relatório após filtro
      frota: carros.size // nº distinto
    };
  }, [filtered]);
  // Expor para conferência no console (debug opcional)
  useEffect(() => {
    window.__debugRowsFiltered = filtered;
  }, [filtered]);
  // agregações por linha (barras)
  const byLinha = useMemo(() => {
    const map = new Map();
    for (const r of filtered) {
      const key = cleanLinha(r.linha) || '—';
      const cur = map.get(key) || { linha: key, passageiros: 0, receita: 0 };

      const pax =
        (r.abt_qtd || 0) + (r.comum_qtd || 0) + (r.esco_grat_qtd || 0) + (r.escolar_qtd || 0) +
        (r.isentos_qtd || 0) + (r.p_social_qtd || 0) + (r.pagante_qtd || 0) + (r.vt_qtd || 0);

      // receita = soma TOTAL de todos os tipos (cada *_valor veio de APLICACOES[].TOTAL)
      const rec =
        (r.abt_valor || 0) + (r.comum_valor || 0) + (r.esco_grat_valor || 0) + (r.escolar_valor || 0) +
        (r.isentos_valor || 0) + (r.p_social_valor || 0) + (r.pagante_valor || 0) + (r.vt_valor || 0);

      cur.passageiros += pax;
      cur.receita += rec;
      map.set(key, cur);
    }
    return [...map.values()].sort((a, b) => b.passageiros - a.passageiros);
  }, [filtered]);
  // Receita Total — card (usa somente TOTAL dos tipos)
  const receitaTotal = useMemo(() => {
    const KEYS = ['abt', 'comum', 'esco_grat', 'escolar', 'isentos', 'p_social', 'pagante', 'vt'];
    let sum = 0;
    for (const r of filtered) {
      for (const k of KEYS) sum += Number(r[`${k}_valor`] || 0);
    }
    return sum;
  }, [filtered]);
  // Tabela: colunas
  const TABLE_COLS = [
    { key: 'dtInicio', label: 'Date', fmt: 'date' },
    { key: 'dtInicio', label: 'Hora', fmt: 'time' },
    { key: 'linha', label: 'Linha', render: (val) => cleanLinha(val) },

    { key: 'carro', label: 'Carro' },
    { key: 'operador', label: 'Operador' },

    { key: 'pagante_qtd', label: 'Pagante', fmt: 'qtd' },
    { key: 'pagante_valor', label: 'Pagante Valor', fmt: 'currency' },

    { key: 'comum_qtd', label: 'Comum', fmt: 'qtd' },
    { key: 'comum_valor', label: 'Comum Valor', fmt: 'currency' },

    { key: 'vt_qtd', label: 'VT', fmt: 'qtd' },
    { key: 'vt_valor', label: 'VT Valor', fmt: 'currency' },

    { key: 'p_social_qtd', label: 'P Social', fmt: 'qtd' },
    { key: 'p_social_valor', label: 'P Social Valor', fmt: 'currency' },

    { key: 'escolar_qtd', label: 'Escolar', fmt: 'qtd' },
    { key: 'escolar_valor', label: 'Escolar Valor', fmt: 'currency' },

    { key: 'esco_grat_qtd', label: 'Esco Grat', fmt: 'qtd' },
    { key: 'esco_grat_valor', label: 'Esco Grat Valor', fmt: 'currency' },

    { key: 'abt_qtd', label: 'ABT', fmt: 'qtd' },
    { key: 'abt_valor', label: 'ABT Valor', fmt: 'currency' },

    { key: 'isentos_qtd', label: 'Isentos', fmt: 'qtd' },

  ];
  // Gráfico de linhas — mesma metodologia dos cards/tabela
  const receitaTipo = useMemo(() => {
    const TYPES = ['abt', 'comum', 'esco_grat', 'escolar', 'isentos', 'p_social', 'pagante', 'vt'];
    const acc = Object.fromEntries(TYPES.map(t => [t, { valor: 0, passageiros: 0 }]));
    for (const r of filtered) {
      for (const t of TYPES) {
        acc[t].valor += Number(r[`${t}_valor`] || 0); // TOTAL do backend
        acc[t].passageiros += Number(r[`${t}_qtd`] || 0);
      }
    }
    return TYPES.map(t => ({
      tipo: t.replace('_', ' ').toUpperCase(),
      valor: acc[t].valor,
      passageiros: acc[t].passageiros,
    }));
  }, [filtered]);
  // dados para o ticker (ordenados por passageiros desc)
  const passengerTickerData = useMemo(() => {
    const totalPass = receitaTipo.reduce((s, x) => s + (x.passageiros || 0), 0);
    return receitaTipo
      .map((x) => ({
        tipo: x.tipo, // "ABT", "COMUM", ...
        passageiros: x.passageiros || 0,
        pct: totalPass > 0 ? (x.passageiros || 0) / totalPass : 0,
        key: String(x.tipo).toLowerCase().replace(/\s+/g, '_'),
      }))
      .sort((a, b) => (b.passageiros || 0) - (a.passageiros || 0));
  }, [receitaTipo]);
  const kpiCards = useMemo(() => ([
    { label: 'Passageiros Totais', value: fmtBR(Math.round(localAgg.passageiros)) },
    { label: 'Receita Total', value: fmtBRL(receitaTotal) },
    // mantém o ticket médio vindo do backend por enquanto
    { label: 'Bilhete Médio Pago', value: fmtBRL(kpis?.ticketMedioPago || 0) },
    {
      label: 'Média Passag./Hora', value: (() => {
        if (!filtered.length) return '0';
        const horas = new Set(filtered.map(r => (r.dtInicio || '').slice(0, 13))).size || 1;
        return (localAgg.passageiros / horas).toFixed(1);
      })()
    },
  ]), [localAgg, receitaTotal, kpis, filtered]);

  // ===== normalização dos dados =====
  // Quando vier APLICACOES, usamos APENAS QTD e TOTAL (metodologia única e central).
  function normalizeRelatorioRows(rowsRaw) {
    const mapTipo = {
      'ABT': 'abt',
      'COMUM': 'comum',
      'ESCO GRAT': 'esco_grat',
      'ESCOLAR': 'escolar',
      'ISENTOS': 'isentos',
      'PAGANTE': 'pagante',
      'P SOCIAL': 'p_social',
      'V.T.': 'vt',
      'V.T': 'vt',
      'VT': 'vt',
    };
    return rowsRaw.map(r => {
      const base = {
        dtInicio: r.dtInicio || r.DT_INICIO || '',
        linha: cleanLinha(r.linha || r.LINHA || ''),
        carro: r.carro || r.CARRO || '',
        operador: r.operador || r.OPERADOR || '',
        abt_qtd: 0, abt_valor: 0,
        comum_qtd: 0, comum_valor: 0,
        esco_grat_qtd: 0, esco_grat_valor: 0,
        escolar_qtd: 0, escolar_valor: 0,
        isentos_qtd: 0, isentos_valor: 0,
        p_social_qtd: 0, p_social_valor: 0,
        pagante_qtd: 0, pagante_valor: 0,
        vt_qtd: 0, vt_valor: 0,
      };

      if (Array.isArray(r.APLICACOES) && r.APLICACOES.length) {
        // usar exclusivamente APLICACOES quando vier
        r.APLICACOES.forEach(a => {
          const key = mapTipo[String(a.APLICACAO || '').toUpperCase()];
          if (!key) return;

          base[`${key}_qtd`] += toNum(a.QTD);
          base[`${key}_valor`] += toNum(a.TOTAL); // <<< SEMPRE usar TOTAL do backend
        });
        return base;
      }

      // fallback: sem APLICACOES, usa campos flat (convertendo para número)
      for (const k of Object.keys(base)) {
        if (k in r && r[k] != null) {
          base[k] = /_qtd$|_valor$/.test(k) ?
            toNum(r[k]) : r[k];
        }
      }
      return base;
    });
  }

  // ações
  async function handleBuscar() {
    setLoading(true);
    try {
      const raw = await postRelatorio(params);
      const data = Array.isArray(raw) ? normalizeRelatorioRows(raw) : [];
      setRows(data);

      const k = await getKpis(params);
      setKpis(k);
    } finally {
      setLoading(false);
    }
  }

  function handleClear() {
    const now = new Date();
    const tomorrow = new Date(now);
    tomorrow.setDate(now.getDate() + 1);

    const fmtDate = (d) => d.toISOString().slice(0, 10);

    // restaurar valores padrão
    setDateIni(fmtDate(now));
    setTimeIni('04:00');
    setDateFim(fmtDate(tomorrow));
    setTimeFim('03:59');
    setLinha('');
    setTipo('');

    // não limpar dados, mas sim recarregar
    handleBuscar();
  }

  // async function handleCsv() {
  //   const blob = await exportCsv(params, 'panel');
  //   saveBlob(blob, `arrecadacao_${garage}_${params.dateIni}_${params.dateFim}_panel.csv`);
  // }
  async function handleXlsx() {
    const blob = await exportXlsx(params);
    saveBlob(blob, `arrecadacao_${garage}_${params.dateIni}_${params.dateFim}.xlsx`);
  }

  // Buscar automaticamente ao entrar na página
  useEffect(() => {
    handleBuscar(); // usa os filtros padrão (hoje 00:00–23:59, garagem 3, etc.)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // ================= render =================
  return (
    <Box sx={{ minHeight: '100dvh', bgcolor: 'background.default' }}>
      {/* ====== SIDEBAR ====== */}
      <Box
        sx={{
          width: { xs: '100%', md: SIDEBAR_W },
          position: { xs: 'relative', md: 'fixed' },
          left: 0,
          top: 0,
          bottom: 0,
          height: { xs: 'auto', md: '100dvh' },
          overflowY: { xs: 'visible', md: 'auto' },
          bgcolor: 'background.paper',
          p: 3,
          borderRight: '1px solid #e0e0e0',
          boxShadow: 3,
          borderRadius: { xs: 0, md: '0 16px 16px 0' },
          zIndex: 10,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 800, color: 'primary.main' }}>
          Filtros
        </Typography>

        <TextField label="Data Início" type="date" value={dateIni}
          onChange={e => setDateIni(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Hora Início" type="time" value={timeIni}
          onChange={e => setTimeIni(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Data Fim" type="date" value={dateFim}
          onChange={e => setDateFim(e.target.value)} InputLabelProps={{ shrink: true }} />
        <TextField label="Hora Fim" type="time" value={timeFim}
          onChange={e => setTimeFim(e.target.value)} InputLabelProps={{ shrink: true }} />

        <FormControl>
          <InputLabel id="linha-label">Linha</InputLabel>
          <Select
            labelId="linha-label"
            value={linha}
            onChange={(e) => setLinha(e.target.value)}
          >
            <MenuItem value="">Todas</MenuItem>
            {linhasOptions.map((ln) => (
              <MenuItem key={ln} value={ln}>{ln}</MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button fullWidth variant="contained" onClick={handleBuscar}
          disabled={loading} sx={{ borderRadius: 2, fontWeight: 700, py: 1.1 }}>
          Buscar
        </Button>
        <Button fullWidth variant="outlined" onClick={handleClear}
          sx={{ borderRadius: 2, fontWeight: 700, py: 1.1 }}>
          Limpar Filtros
        </Button>

        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            fullWidth
            variant="contained"
            onClick={handleVoltar}
            sx={{ borderRadius: 2, fontWeight: 700, bgcolor: '#757575', '&:hover': { bgcolor: '#5a5a5a' } }}
          >
            Voltar
          </Button>

          <Button fullWidth variant="outlined" onClick={handleXlsx} disabled={!rows.length}
            sx={{ borderRadius: 2, fontWeight: 700 }}>
            XLSX
          </Button>

          <IconButton onClick={toggleTheme}>
            {mode === 'dark' ? <LightModeIcon /> : <DarkModeIcon />}
          </IconButton>
        </Stack>
      </Box>

      {/* ====== CONTEÚDO PRINCIPAL (à direita) ====== */}
      <Box sx={{ flex: 1, p: 4, ml: { xs: 0, md: `${SIDEBAR_W}px` } }}>
        {/* KPIs */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          {kpiCards.map(({ label, value }, idx) => (
            <Paper
              sx={{
                p: 2, minWidth: 240, borderRadius: 2, boxShadow: 3,
                borderLeft: '6px solid #1976d2',
                bgcolor: 'background.paper', // 🔄 tema
              }}
            >
              <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>{value}</Typography>
              <Typography variant="body2" color="text.secondary">{label}</Typography>
            </Paper>
          ))}
        </Box>

        {/* KPIs por Tipo de Passagem (formato dos cards principais) */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', flexWrap: 'wrap', gap: 2, mb: 3 }}>
          {Object.entries(CHIP_COLORS)
            .filter(([k]) => k.toLowerCase() !== 'isentos') // <-- remove
            .map(([k, c]) => {
              const label = k.replace('_', ' ').toUpperCase();
              const d = receitaTipo.find((x) => x.tipo === label) || { valor: 0, passageiros: 0 };
              const pct = receitaTotal > 0 ? d.valor / receitaTotal : 0;
              return (
                <Paper
                  sx={{
                    p: 2, minWidth: 240, borderRadius: 2, boxShadow: 3,
                    bgcolor: 'background.paper', // 🔄 tema
                    borderLeft: `6px solid ${c}`,
                  }}
                >
                  {/* valor grande (Receita do tipo) */}
                  <Typography variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
                    {fmtBRL(d.valor)}
                  </Typography>

                  {/* legenda pequena: NOME DO TIPO + % do total */}
                  <Typography variant="body2" color="text.secondary">
                    {label} — {fmtPct(pct)} do total
                  </Typography>
                </Paper>
              );
            })}
        </Box>

        {/* Ticker de passageiros e % */}
        <Box sx={{ mt: 1, mb: 2 }}>
          <PassengerTicker data={passengerTickerData} colors={CHIP_COLORS} />
        </Box>

        {/* Gráficos */}
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(12, 1fr)', gap: 2, mb: 2 }}>
          <Paper
            sx={{
              gridColumn: { xs: '1 / -1', md: '1 / span 6' },
              p: 3, // Aumentando o padding para mais espaço interno.
              borderRadius: 3, // Aumentando o border-radius para um visual mais suave.
              boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.1)', // Adicionando uma sombra mais pronunciada.
              height: 380, // Aumentando a altura para melhor visualização.
              bgcolor: 'background.paper', // Usando a cor de fundo do tema MUI.
            }}
          >
            <Typography
              variant="h5" // Usando um tamanho de fonte maior para o título.
              sx={{
                fontWeight: 800,
                textAlign: 'center',
                textTransform: 'uppercase',
                mb: 2, // Aumentando o margin-bottom.
                color: 'text.primary',
              }}
            >
              Passageiros por Linha
            </Typography>

            <ResponsiveContainer width="100%" height="85%">
              <BarChart
                data={byLinha}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="colorPassengers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#25A69A" stopOpacity={0.9} /> {/* Usando um tom de verde mais moderno e escuro. */}
                    <stop offset="95%" stopColor="#5EDF72" stopOpacity={0.8} /> {/* Adicionando um segundo tom para o gradiente. */}
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.6} /> {/* Grade mais suave e apenas horizontal. */}

                <XAxis
                  dataKey="linha"
                  tick={{ fontSize: 11, fill: mode === 'dark' ? '#fff' : '#666' }}
                />
                <YAxis
                  tickFormatter={fmtBR}
                  tick={{ fontSize: 11, fill: mode === 'dark' ? '#fff' : '#666' }}
                />

                <Tooltip
                  contentStyle={{
                    backgroundColor: mode === 'dark'
                      ? 'rgba(30, 30, 30, 0.9)'
                      : 'rgba(255, 255, 255, 0.9)',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.2)',
                    color: mode === 'dark' ? '#fff' : '#000',
                  }}
                />

                <Bar
                  dataKey="passageiros"
                  fill="url(#colorPassengers)" // Usando o gradiente definido.
                  gradiente //definido.
                  barSize={20} // Ajustando a largura das barras para um visual mais clean.
                  radius={[5, 5, 0, 0]} // Arredondando apenas a parte superior das barras.
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          <Paper sx={{ gridColumn: { xs: '1 / -1', md: '7 / -1' }, p: 3, borderRadius: 3, boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.1)', height: 380, bgcolor: 'background.paper' }}>
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                textAlign: 'center',
                textTransform: 'uppercase',
                mb: 2,
                color: 'text.primary',
              }}
            >
              Receita Por Linha
            </Typography>
            <ResponsiveContainer width="100%" height="85%">
              <BarChart
                data={[...byLinha].sort((a, b) => b.receita
                  - a.receita)}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%" stopColor="#B967FF" stopOpacity={0.9} />
                    <stop offset="95%" stopColor="#D8A9FF" stopOpacity={0.8} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.6} />
                <XAxis
                  dataKey="linha"
                  tick={{ fontSize: 11, fill: mode === 'dark' ? '#fff' : '#666' }}
                  axisLine={false}
                  tickLine={false}
                />

                <YAxis
                  tickFormatter={v => fmtBRL(v)}
                  width={80}
                  tick={{ fontSize: 11, fill: mode === 'dark' ? '#fff' : '#666' }}
                  axisLine={false}
                  tickLine={false}
                />

                <Tooltip
                  cursor={{ fill: 'rgba(0, 0, 0, 0.05)' }}
                  contentStyle={{
                    backgroundColor: mode === 'dark' ? 'rgba(30,30,30,.9)' : 'rgba(255,255,255,.9)',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  }}
                  formatter={v => fmtBRL(v)}
                />
                <Bar
                  dataKey="receita"
                  fill="url(#colorRevenue)"
                  barSize={20}
                  radius={[5, 5, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </Paper>

          <Paper
            sx={{
              gridColumn: '1 / -1',
              p: 3, // Aumentando o padding para mais espaço interno.
              borderRadius: 3, // Aumentando o border-radius para um visual mais suave.
              boxShadow: '0px 10px 15px -3px rgba(0,0,0,0.1)', // Adicionando uma sombra mais pronunciada.
              height: 380, // Aumentando a altura para melhor visualização.
              bgcolor: 'background.paper', // Usando a cor de fundo do tema MUI.
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontWeight: 800,
                mb: 2, // Aumentando o margin-bottom.
                textAlign: 'center',
                textTransform: 'uppercase',
                color: 'text.primary',
              }}
            >
              Receita por Tipo de Passagem
            </Typography>

            <ResponsiveContainer width="100%" height="85%">
              <LineChart
                data={receitaTipo}
                margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
              >
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="colorPassengers" x1="0"
                    y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                  </linearGradient>
                </defs>

                <CartesianGrid strokeDasharray="3 3" vertical={false} strokeOpacity={0.6} /> {/* Efeito de grade mais suave e apenas horizontal. */}

              <XAxis
                dataKey="tipo"
                tick={{ fontSize: 12, fill: mode === 'dark' ? '#fff' : '#666' }}
                axisLine={false}
                tickLine={false}
              />

              <YAxis
                yAxisId="left"
                tickFormatter={(v) => fmtBRL(v)}
                width={85}
                tick={{ fontSize: 12, fill: mode === 'dark' ? '#fff' : '#666' }}
                axisLine={false}
                tickLine={false}
              />

              <YAxis
                yAxisId="right"
                tickFormatter={(v) => fmtBR(v)}
                orientation="right"
                width={60}
                tick={{ fontSize: 12, fill: mode === 'dark' ? '#fff' : '#666' }}
                axisLine={false}
                tickLine={false}
              />

                <Tooltip
                  cursor={{ strokeDasharray: '3 3' }}
                  contentStyle={{
                    backgroundColor: mode === 'dark' ? 'rgba(30,30,30,.9)' : 'rgba(255,255,255,.9)',
                    border: 'none',
                    borderRadius: '8px',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                  }}
                  formatter={(v, name) => (name === 'Receita (R$)' ? fmtBRL(v) : fmtBR(v))}
                />

                <Legend
                  wrapperStyle={{ bottom: -20, left: 0 }} // Posicionando a legenda.
                  iconType="circle" // Mudando o ícone da legenda.
                />

                <Line
                  yAxisId="left"
                  type="monotone"
                  dataKey="valor"
                  name="Receita (R$)"
                  stroke="#82ca9d" // Cor da linha mais agradável.
                  strokeWidth={3} // Linha mais grossa.
                  dot={{ stroke: '#82ca9d', strokeWidth: 2, r: 4 }} // Ponto mais visível.
                  activeDot={{ r: 8, stroke: '#82ca9d', strokeWidth: 3 }} // Ponto ativado ao passar o mouse.
                />

                <Line
                  yAxisId="right"
                  type="monotone"
                  dataKey="passageiros"
                  name="Passageiros"
                  stroke="#8884d8"
                  strokeWidth={3}
                  dot={{ stroke: '#8884d8', strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 8, stroke: '#8884d8', strokeWidth: 3 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>

          {/* Histórico de Arrecadação */}
          <GraficosArrecad
            mode={mode}
            garage={garage}
            dateIni={dateIni}
            tipoSelecionado={tipo}
          />
        </Box>
        
        <Box sx={{ mt: 2 }}>
          <GraficosPassageiros
            mode={mode}
            garage={garage}
            dateIni={dateIni}
            tipoSelecionado={tipo}
          />
        </Box>

        {/* Relatório Diário */}
        <Paper sx={{ p: 2, borderRadius: 2, boxShadow: 3, mt: 3 }}>
          <Typography
            variant="subtitle1"
            sx={{ fontWeight: 800, mb: 1, textAlign: 'center', textTransform: 'uppercase' }}
          >
            Relatório Diário
          </Typography>
          <Divider sx={{ mb: 1 }} />
          {!filtered.length ?
            (
              <Typography color="text.secondary" sx={{ textAlign: 'center' }}>
                Nenhum dado. Ajuste os filtros e clique em Buscar.
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 520 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      {TABLE_COLS.map(col => (
                        <TableCell
                          key={col.key + col.label}
                          sx={{
                            fontWeight: 800,
                            whiteSpace: 'nowrap',
                            textAlign: 'center',
                            textTransform: 'uppercase',
                          }}
                        >
                          {col.label.replace(/_/, ' ')}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((r, i) => (
                      <TableRow key={i} hover>
                        {TABLE_COLS.map(col => {
                          const raw = r[col.key];
                          let rendered = raw;

                          if (col.key === 'dtInicio') {
                            const [date, time] = raw.split(' ');
                            rendered = col.fmt === 'date' ? date : time;
                          } else {
                            if (col.render) {
                              rendered = col.render(raw, r);
                            } else if (col.fmt === 'currency') {
                              rendered = fmtBRL(raw);
                            } else if (col.fmt === 'qtd') {
                              rendered = fmtBR(raw);
                            } else {
                              rendered = raw;
                            }
                          }

                          return (
                            <TableCell key={col.key} sx={{ textAlign: 'center' }}>
                              {rendered}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
        </Paper>

      </Box> {/* Fechando o Box principal */}
    </Box>
  );
}